import React from 'react';
import axios from 'axios';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

import {
    FaAward,
    FaTrophy,
    FaFileAlt,
    FaUsers,
    FaCheckCircle,
    FaTimesCircle,
    FaClock,
    FaChartLine
} from 'react-icons/fa';

import { FcStatistics } from 'react-icons/fc';

import apiUrl from './base_url';
import HOC from './HOC';

import '../components/cssfile/result.css';

class Result extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            analysis: [],
            rank: [],
            isLoading: true,
            error: ''
        };
    }

    componentDidMount() {
        const regid =
            this.props.location.state?.regid ||
            this.props.location.state?.data?.regid;

        const exam_id =
            this.props.location.state?.id ||
            this.props.location.state?.data?.exam_id;

        if (!regid || !exam_id) {
            this.setState({
                isLoading: false,
                error: 'Result information is missing.'
            });
            return;
        }

        Promise.all([
            axios.get(
                `${apiUrl}serverside/analysis.php?exam_id=${exam_id}&regid=${regid}`
            ),
            axios.get(
                `${apiUrl}serverside/rank.php?exam_id=${exam_id}&regid=${regid}`
            )
        ])
            .then(([analysisResponse, rankResponse]) => {

                this.setState({
                    analysis: Array.isArray(analysisResponse.data)
                        ? analysisResponse.data
                        : [],

                    rank: Array.isArray(rankResponse.data)
                        ? rankResponse.data
                        : [],

                    isLoading: false
                });

            })
            .catch(error => {

                console.error('Result API Error:', error);

                this.setState({
                    isLoading: false,
                    error: 'Unable to load result data.'
                });

            });
    }

    getNumber(value, fallback = 0) {
        const number = Number(value);

        return Number.isFinite(number)
            ? number
            : fallback;
    }

    formatPercent(value) {
        return `${this.getNumber(value).toFixed(2)}%`;
    }

    renderMetricCard(icon, label, value, subtitle, className = '') {

        return (
            <div className={`result-stat-card ${className}`}>

                <div className="result-stat-icon">
                    {icon}
                </div>

                <div className="result-stat-content">

                    <span className="result-stat-label">
                        {label}
                    </span>

                    <strong className="result-stat-value">
                        {value}
                    </strong>

                    {subtitle && (
                        <span className="result-stat-subtitle">
                            {subtitle}
                        </span>
                    )}

                </div>

            </div>
        );
    }

    renderPerformanceChart(title, achieved, total, suffix = '') {

        const safeTotal = Math.max(
            this.getNumber(total),
            this.getNumber(achieved),
            1
        );

        const safeAchieved = Math.min(
            Math.max(this.getNumber(achieved), 0),
            safeTotal
        );

        const remaining = Math.max(
            safeTotal - safeAchieved,
            0
        );

        const data = [
            {
                name: 'Achieved',
                value: safeAchieved
            },
            {
                name: 'Remaining',
                value: remaining
            }
        ];

        return (
            <div className="result-chart-card">

                <div className="result-chart-header">

                    <div>
                        <span className="result-chart-eyebrow">
                            PERFORMANCE
                        </span>

                        <h3>
                            {title}
                        </h3>
                    </div>

                    <div className="result-chart-value">
                        {this.getNumber(achieved)}
                        {suffix}
                    </div>

                </div>

                <div className="result-chart-wrapper">

                    <ResponsiveContainer
                        width="100%"
                        height={210}
                    >

                        <PieChart>

                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                innerRadius={58}
                                outerRadius={78}
                                paddingAngle={3}
                                dataKey="value"
                                stroke="none"
                            >

                                {data.map((entry, index) => (

                                    <Cell
                                        key={`cell-${index}`}
                                        fill={
                                            index === 0
                                                ? '#2563eb'
                                                : '#e8eef7'
                                        }
                                    />

                                ))}

                            </Pie>

                            <Tooltip
                                formatter={(value) => [
                                    value,
                                    'Questions'
                                ]}
                            />

                        </PieChart>

                    </ResponsiveContainer>

                    <div className="result-chart-center">

                        <strong>
                            {safeTotal > 0
                                ? `${((safeAchieved / safeTotal) * 100).toFixed(0)}%`
                                : '0%'
                            }
                        </strong>

                        <span>
                            Completed
                        </span>

                    </div>

                </div>

            </div>
        );
    }

    render() {

        const {
            analysis,
            rank,
            isLoading,
            error
        } = this.state;

        if (isLoading) {

            return (
                <div className="result-page">

                    <div className="result-loading">

                        <div className="result-loading-spinner" />

                        <h3>
                            Loading your result
                        </h3>

                        <p>
                            Please wait while we prepare your performance summary.
                        </p>

                    </div>

                </div>
            );
        }

        if (error) {

            return (
                <div className="result-page">

                    <div className="result-error">

                        <div className="result-error-icon">
                            !
                        </div>

                        <h3>
                            Result unavailable
                        </h3>

                        <p>
                            {error}
                        </p>

                    </div>

                </div>
            );
        }

        const result = analysis[0] || {};
        const ranking = rank[0] || {};

        /* =========================
           RESULT VALUES
        ========================= */

        const totalStudents =
            this.getNumber(ranking.total_students, 0);

        const rankObtained =
            this.getNumber(ranking.rank, 0);

        const percentile =
            this.getNumber(ranking.percentile, 0);

        const totalScore =
            this.getNumber(result.total_score, 0);

        const scoreObtained =
            this.getNumber(result.total_score_obtained, 0);

        const totalQuestions =
            this.getNumber(result.total_q, 0);

        const attemptedQuestions =
            this.getNumber(result.total_qid, 0);

        const correctAnswers =
            this.getNumber(result.total_correct, 0);

        const incorrectAnswers =
            this.getNumber(result.total_incorrect, 0);

        const accuracy =
            this.getNumber(result.accuracy, 0);

        const totalTime =
            Math.floor(
                this.getNumber(result.time, 0) / 60000
            );

        const timeTaken =
            Math.floor(
                (
                    this.getNumber(result.time, 0) -
                    this.getNumber(result.time_taken, 0)
                ) / 60000
            );

        const safeTimeTaken =
            Math.max(timeTaken, 0);

        const rankPercentage =
            totalStudents > 0
                ? ((totalStudents - rankObtained + 1) / totalStudents) * 100
                : 0;

        return (

            <div className="result-page">

                {/* =========================
                    HEADER
                ========================= */}

                <div className="result-header">

                    <div className="result-header-content">

                        <div>

                            <span className="result-eyebrow">
                                EXAMINATION RESULT
                            </span>

                            <h1>
                                Performance Summary
                            </h1>

                            <p>
                                Review your performance, rank and overall examination statistics.
                            </p>

                        </div>

                        <div className="result-header-icon">
                            <FaChartLine />
                        </div>

                    </div>

                </div>


                <div className="result-container">

                    {/* =========================
                        RANK HERO
                    ========================= */}

                    <section className="rank-hero">

                        <div className="rank-hero-main">

                            <div className="rank-trophy">
                                <FaTrophy />
                            </div>

                            <div>

                                <span className="rank-label">
                                    YOUR RANK
                                </span>

                                <div className="rank-number">
                                    #{rankObtained}
                                </div>

                                <span className="rank-total">
                                    out of {totalStudents} students
                                </span>

                            </div>

                        </div>


                        <div className="rank-performance">

                            <div className="rank-performance-item">

                                <span>
                                    Percentile
                                </span>

                                <strong>
                                    {this.formatPercent(percentile)}
                                </strong>

                            </div>

                            <div className="rank-performance-divider" />

                            <div className="rank-performance-item">

                                <span>
                                    Score
                                </span>

                                <strong>
                                    {scoreObtained}/{totalScore}
                                </strong>

                            </div>

                        </div>

                    </section>


                    {/* =========================
                        STATISTICS
                    ========================= */}

                    <section className="result-statistics">

                        <div className="result-section-heading">

                            <div>

                                <span>
                                    OVERVIEW
                                </span>

                                <h2>
                                    Your Performance
                                </h2>

                            </div>

                        </div>


                        <div className="result-stat-grid">

                            {this.renderMetricCard(
                                <FaAward />,
                                'Rank',
                                `#${rankObtained}`,
                                `${totalStudents} students`,
                                'stat-rank'
                            )}

                            {this.renderMetricCard(
                                <FaTrophy />,
                                'Score',
                                `${scoreObtained}/${totalScore}`,
                                'Total score',
                                'stat-score'
                            )}

                            {this.renderMetricCard(
                                <FaFileAlt />,
                                'Attempted',
                                `${attemptedQuestions}/${totalQuestions}`,
                                'Questions attempted',
                                'stat-attempted'
                            )}

                            {this.renderMetricCard(
                                <FaUsers />,
                                'Percentile',
                                this.formatPercent(percentile),
                                'Overall percentile',
                                'stat-percentile'
                            )}

                            {this.renderMetricCard(
                                <FcStatistics />,
                                'Accuracy',
                                this.formatPercent(accuracy),
                                'Answer accuracy',
                                'stat-accuracy'
                            )}

                            {this.renderMetricCard(
                                <FaCheckCircle />,
                                'Correct',
                                correctAnswers,
                                `out of ${totalQuestions}`,
                                'stat-correct'
                            )}

                            {this.renderMetricCard(
                                <FaTimesCircle />,
                                'Incorrect',
                                incorrectAnswers,
                                `out of ${totalQuestions}`,
                                'stat-incorrect'
                            )}

                            {this.renderMetricCard(
                                <FaClock />,
                                'Time Taken',
                                `${safeTimeTaken} min`,
                                `of ${totalTime} min`,
                                'stat-time'
                            )}

                        </div>

                    </section>


                    {/* =========================
                        CHARTS
                    ========================= */}

                    <section className="result-analysis">

                        <div className="result-section-heading">

                            <div>

                                <span>
                                    ANALYSIS
                                </span>

                                <h2>
                                    Performance Breakdown
                                </h2>

                            </div>

                        </div>


                        <div className="result-chart-grid">

                            {this.renderPerformanceChart(
                                'Rank',
                                rankObtained > 0
                                    ? totalStudents - rankObtained + 1
                                    : 0,
                                totalStudents
                            )}

                            {this.renderPerformanceChart(
                                'Score',
                                scoreObtained,
                                totalScore
                            )}

                            {this.renderPerformanceChart(
                                'Attempted',
                                attemptedQuestions,
                                totalQuestions
                            )}

                            {this.renderPerformanceChart(
                                'Percentile',
                                percentile,
                                100,
                                '%'
                            )}

                            {this.renderPerformanceChart(
                                'Accuracy',
                                accuracy,
                                100,
                                '%'
                            )}

                            {this.renderPerformanceChart(
                                'Correct Answers',
                                correctAnswers,
                                totalQuestions
                            )}

                            {this.renderPerformanceChart(
                                'Incorrect Answers',
                                incorrectAnswers,
                                totalQuestions
                            )}

                            {this.renderPerformanceChart(
                                'Time Taken',
                                safeTimeTaken,
                                totalTime,
                                ' min'
                            )}

                        </div>

                    </section>


                    {/* =========================
                        RANK FOOTER
                    ========================= */}

                    <section className="rank-summary">

                        <div className="rank-summary-icon">
                            <FaAward />
                        </div>

                        <div className="rank-summary-content">

                            <span>
                                FINAL RANK
                            </span>

                            <strong>
                                #{rankObtained}
                            </strong>

                            <p>
                                You ranked #{rankObtained} among{' '}
                                {totalStudents} students.
                            </p>

                        </div>

                        <div className="rank-summary-percent">

                            <strong>
                                {this.formatPercent(percentile)}
                            </strong>

                            <span>
                                Percentile
                            </span>

                        </div>

                    </section>

                </div>

            </div>
        );
    }
}

export default HOC(Result);